import { signatureOf } from '../signature.js';
import { resolvePresetToAnimation } from '../compiler/css-animations.js';
/** Slide travel distance, in px, matching the base seed's authored offsets. */
const SLIDE_DISTANCE = 100;
/**
 * Combination entrances travel a SHORTER distance than a bare slide. A full 100px
 * glide reads as a slide; the fade is what carries the entrance, so the movement
 * is a nudge that gives it direction.
 */
const NUDGE_DISTANCE = 40;
/**
 * Motions offered in the grid, in the UI's display order. The keyframe shapes
 * mirror `base-presets.json` exactly (so a preset-authored effect and the same
 * effect authored by prompt compile identically — locked by a test).
 */
export const MOTION_LIBRARY = Object.freeze([
    {
        id: 'fadeIn',
        family: 'entrance',
        type: 'fadeIn',
        from: { opacity: 0 },
        to: { opacity: 1 },
        defaultTiming: { delay: 0, duration: 600, easing: 'ease-out' },
    },
    {
        id: 'slideUp',
        family: 'entrance',
        type: 'slideUp',
        from: { y: SLIDE_DISTANCE },
        to: { y: 0 },
        defaultTiming: { delay: 0, duration: 600, easing: 'ease-out' },
        direction: 'up',
    },
    {
        id: 'slideDown',
        family: 'entrance',
        type: 'slideDown',
        from: { y: -SLIDE_DISTANCE },
        to: { y: 0 },
        defaultTiming: { delay: 0, duration: 600, easing: 'ease-out' },
        direction: 'down',
    },
    {
        id: 'slideLeft',
        family: 'entrance',
        type: 'slideLeft',
        from: { x: SLIDE_DISTANCE },
        to: { x: 0 },
        defaultTiming: { delay: 0, duration: 600, easing: 'ease-out' },
        direction: 'left',
    },
    {
        id: 'slideRight',
        family: 'entrance',
        type: 'slideRight',
        from: { x: -SLIDE_DISTANCE },
        to: { x: 0 },
        defaultTiming: { delay: 0, duration: 600, easing: 'ease-out' },
        direction: 'right',
    },
    {
        id: 'pop',
        family: 'entrance',
        type: 'scale',
        from: { scaleX: 0, scaleY: 0 },
        to: { scaleX: 1, scaleY: 1 },
        defaultTiming: { delay: 0, duration: 500, easing: 'ease-out' },
    },
    {
        id: 'zoomIn',
        family: 'entrance',
        type: 'scale',
        from: { scaleX: 0.8, scaleY: 0.8 },
        to: { scaleX: 1, scaleY: 1 },
        defaultTiming: { delay: 0, duration: 600, easing: 'ease-out' },
    },
    // ——— Combination entrances (opacity + a directional nudge, ONE animation) ———
    {
        id: 'fadeInUp',
        family: 'entrance',
        type: 'slideUp',
        from: { opacity: 0, y: NUDGE_DISTANCE },
        to: { opacity: 1, y: 0 },
        defaultTiming: { delay: 0, duration: 700, easing: 'ease-out' },
        direction: 'up',
    },
    {
        id: 'fadeInDown',
        family: 'entrance',
        type: 'slideDown',
        from: { opacity: 0, y: -NUDGE_DISTANCE },
        to: { opacity: 1, y: 0 },
        defaultTiming: { delay: 0, duration: 700, easing: 'ease-out' },
        direction: 'down',
    },
    {
        id: 'fadeInLeft',
        family: 'entrance',
        type: 'slideLeft',
        from: { opacity: 0, x: NUDGE_DISTANCE },
        to: { opacity: 1, x: 0 },
        defaultTiming: { delay: 0, duration: 700, easing: 'ease-out' },
        direction: 'left',
    },
    {
        id: 'fadeInRight',
        family: 'entrance',
        type: 'slideRight',
        from: { opacity: 0, x: -NUDGE_DISTANCE },
        to: { opacity: 1, x: 0 },
        defaultTiming: { delay: 0, duration: 700, easing: 'ease-out' },
        direction: 'right',
    },
    {
        id: 'bounceIn',
        family: 'entrance',
        type: 'bounce',
        from: { scaleX: 0, scaleY: 0 },
        to: { scaleX: 1, scaleY: 1 },
        // The springy curve overshoots past 1 and settles back — that overshoot IS the bounce.
        defaultTiming: { delay: 0, duration: 700, easing: 'cubic-bezier(0.34,1.56,0.64,1)' },
    },
    {
        id: 'rotateIn',
        family: 'entrance',
        type: 'rotate',
        from: { opacity: 0, rotation: -90 },
        to: { opacity: 1, rotation: 0 },
        defaultTiming: { delay: 0, duration: 700, easing: 'ease-out' },
    },
    // ——— Exits. The compiler emits `animation-fill-mode: both`, so the element HOLDS
    // its final state: an exit really does leave it invisible/off-position for good. ———
    {
        id: 'fadeOut',
        family: 'exit',
        type: 'fadeOut',
        from: { opacity: 1 },
        to: { opacity: 0 },
        defaultTiming: { delay: 0, duration: 600, easing: 'ease-in' },
    },
    {
        id: 'slideOutUp',
        family: 'exit',
        type: 'slideUp',
        from: { opacity: 1, y: 0 },
        to: { opacity: 0, y: -NUDGE_DISTANCE },
        defaultTiming: { delay: 0, duration: 600, easing: 'ease-in' },
        direction: 'up',
    },
    {
        id: 'slideOutDown',
        family: 'exit',
        type: 'slideDown',
        from: { opacity: 1, y: 0 },
        to: { opacity: 0, y: NUDGE_DISTANCE },
        defaultTiming: { delay: 0, duration: 600, easing: 'ease-in' },
        direction: 'down',
    },
    // ——— Loops. `iterations: 'infinite'` is what makes these possible at all; before
    // it existed a "pulse" could only grow the element and abandon it there.
    // `direction: 'alternate'` plays every other pass backwards, so the element
    // returns to where it started instead of snapping back. A marquee is the one
    // exception — it must restart from the same edge, so it stays `normal`. ———
    {
        id: 'loopPulse',
        family: 'loop',
        type: 'pulse',
        from: { scaleX: 1, scaleY: 1 },
        to: { scaleX: 1.08, scaleY: 1.08 },
        defaultTiming: {
            delay: 0,
            duration: 900,
            easing: 'ease-in-out',
            iterations: 'infinite',
            direction: 'alternate',
        },
    },
    {
        id: 'loopBlink',
        family: 'loop',
        type: 'pulse',
        from: { opacity: 1 },
        to: { opacity: 0.35 },
        defaultTiming: {
            delay: 0,
            duration: 800,
            easing: 'ease-in-out',
            iterations: 'infinite',
            direction: 'alternate',
        },
    },
    {
        id: 'loopFloat',
        family: 'loop',
        type: 'pulse',
        from: { y: 0 },
        to: { y: -10 },
        defaultTiming: {
            delay: 0,
            duration: 1600,
            easing: 'ease-in-out',
            iterations: 'infinite',
            direction: 'alternate',
        },
    },
    {
        id: 'loopMarquee',
        family: 'loop',
        type: 'slideLeft',
        // Placeholder shape — `travel: 'acrossCanvas'` recomputes both ends per element.
        from: { x: 0 },
        to: { x: 0 },
        defaultTiming: { delay: 0, duration: 6000, easing: 'linear', iterations: 'infinite' },
        direction: 'left',
        travel: 'acrossCanvas',
    },
    // ——— Ambient background drift (Epic 55). Two rules make these safe, and both are
    // load-bearing:
    //
    //   1. `from` IS THE IDENTITY TRANSFORM. Anything else and the settled banner is
    //      permanently displaced from the static design — a background parked at
    //      scale 1.06 is not the design the client approved.
    //   2. `iterations` is FINITE AND EVEN, with `direction: 'alternate'`. The last
    //      pass runs backwards, so the element lands back on `from` — the identity —
    //      and `fill-mode: both` holds it there. An odd count would settle on `to`.
    //
    // `softPan` drifts AND scales together rather than translating alone: a bare
    // translate would slide a full-bleed background off its own edge and show a strip
    // of canvas. Growing to 1.05 while moving 12px keeps ~27px of overhang on a
    // 1080-wide element, which covers the move at every point along it.
    {
        id: 'softZoom',
        family: 'loop',
        type: 'pulse',
        from: { scaleX: 1, scaleY: 1 },
        to: { scaleX: 1.04, scaleY: 1.04 },
        defaultTiming: {
            delay: 0,
            duration: 2200,
            easing: 'ease-in-out',
            iterations: 2,
            direction: 'alternate',
        },
        hidden: true,
    },
    {
        id: 'softPan',
        family: 'loop',
        type: 'pulse',
        from: { x: 0, scaleX: 1, scaleY: 1 },
        // `to.x` is a PLACEHOLDER — `travel: 'withinOverscale'` recomputes it per element
        // from that element's own width and this scale. See `travel`'s own doc.
        to: { x: 0, scaleX: 1.05, scaleY: 1.05 },
        travel: 'withinOverscale',
        defaultTiming: {
            delay: 0,
            duration: 2200,
            easing: 'ease-in-out',
            iterations: 2,
            direction: 'alternate',
        },
        hidden: true,
    },
]);
export function findMotion(id) {
    return MOTION_LIBRARY.find((m) => m.id === id);
}
/** Build the `AnimationPreset` a motion + timing choice denotes. Pure. */
export function motionToPreset(motion, timing) {
    return {
        type: motion.type,
        from: motion.from,
        to: motion.to,
        timing,
        ...(motion.direction !== undefined ? { params: { direction: motion.direction } } : {}),
    };
}
/**
 * `cubic-bezier(x1, y1, x2, y2)` with x-coordinates in [0,1] — the CSS spec's
 * constraint, and the one the definition schema does NOT enforce (`easing` is a
 * bare `z.string()`), so an invalid string would otherwise reach CSS verbatim.
 */
const KEYWORD_EASINGS = ['linear', 'ease-in', 'ease-out', 'ease-in-out'];
export function isValidEasing(easing) {
    const value = easing.trim();
    if (KEYWORD_EASINGS.includes(value))
        return true;
    const match = /^cubic-bezier\(([^)]*)\)$/.exec(value);
    if (!match)
        return false;
    const parts = (match[1] ?? '').split(',').map((p) => Number(p.trim()));
    if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n)))
        return false;
    const x1 = parts[0];
    const x2 = parts[2];
    return x1 >= 0 && x1 <= 1 && x2 >= 0 && x2 <= 1;
}
/**
 * Resolve a preset choice into `Animation[]` — **no model call, no network**.
 *
 * Binds each animation to its element's home position: presets author x/y as
 * home-relative offsets and the compiler emits `translate(value − home)`, so an
 * unbound slide lands a full home-offset off-position (DBG-005).
 *
 * Throws on an unknown motion, an unknown element, an invalid easing, or a
 * definition with no scene — every one of which is a caller bug, not a user error.
 */
export function buildPresetAnimations(definition, request) {
    const motion = findMotion(request.motionId);
    if (!motion)
        throw new Error(`Unknown motion: ${request.motionId}`);
    const scene = definition.scenes[0]?.id;
    if (!scene)
        throw new Error('Cannot author animations: definition has no scenes');
    const timing = { ...motion.defaultTiming, ...request.timing };
    if (!isValidEasing(timing.easing))
        throw new Error(`Invalid easing: ${timing.easing}`);
    const animations = [];
    for (const elementId of request.targetElementIds) {
        const element = definition.elements.find((e) => e.id === elementId);
        if (!element)
            throw new Error(`Unknown element: ${elementId}`);
        // A marquee must cross the whole banner, so its travel depends on the canvas and
        // on this element's own width — it cannot be a constant in the table. It enters
        // from just past the right edge and leaves just past the left one, measured
        // RELATIVE to the element's home (the compiler emits translate(value − home)).
        const shaped = motion.travel === 'acrossCanvas'
            ? {
                ...motion,
                from: { x: definition.canvas.width - element.position.x },
                to: { x: -(element.position.x + element.position.width) },
            }
            : motion.travel === 'withinOverscale'
                ? {
                    // Travel exactly as far as this element overhangs its own box once
                    // scaled — the largest move that cannot show the canvas edge.
                    ...motion,
                    to: {
                        ...motion.to,
                        x: (element.position.width * ((motion.to.scaleX ?? 1) - 1)) / 2,
                    },
                }
                : motion;
        const preset = motionToPreset(shaped, timing);
        animations.push(resolvePresetToAnimation(preset, {
            id: buildAnimationId(elementId, motion.type, animations),
            targetElement: elementId,
            scene,
            presetSignature: signatureOf(preset),
            position: { x: element.position.x, y: element.position.y },
        }));
    }
    return animations;
}
/** Unique, sanitized animation id — mirrors the renderer's `anim-{el}-{type}` convention. */
function buildAnimationId(elementId, type, existing) {
    const safeType = type.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const base = `anim-${elementId}-${safeType}`;
    if (!existing.some((a) => a.id === base))
        return base;
    let n = 2;
    while (existing.some((a) => a.id === `${base}-${n}`))
        n++;
    return `${base}-${n}`;
}
